import CryptoJS from "crypto-js";
import { Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppButton from "../../UI/AppButtons";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import { CarPopUpProps } from "./BookingVehicleInfoCard.props";
import BookingGetters from "../../../store/onlineBooking/getters";
import i18n from "../../../config/i18n";
import { Box, Typography, Tabs, Tab, Grid, IconButton } from "@mui/material";
import { formatPriceWithCurrencySymbol } from "../../../helpers/getPriceWithCurrencySymbol";
import {
  buildPayLaterSubLabel,
  buildPayNowDiscountLabel,
  computeApproximateDaysFromDates,
  computePaymentChoiceOnCard,
  computeVoucherOriginalPaymentChoice,
} from "../../../helpers/paymentChoiceOnCard";
import { PaymentChoiceBlock } from "./PaymentChoiceBlock";
function CarDetailsPopup({
  localeScalapay,
  scriptChannel,
  VehicleDetails,
  onClose,
  dynamicTheme,
  totalPricePerDay,
  modalTechnicalDataContent,
  modalDeductibleDepositContent,
  paymentType,
  totalPrice,
  selectedFields,
  payNowDisAmount,

  vehicleInternationalCode,
  isVehicleUnavQuot,
  closeModal = () => {},
  setIsLoading = () => {},
  onScalapayWidgetChange = () => {},
}: CarPopUpProps) {
  const currency = BookingGetters.getCurrency();
  const discountedAmountDetails = formatPriceWithCurrencySymbol(
    currency,
    totalPricePerDay
  );
  const amountDetails = formatPriceWithCurrencySymbol(currency, totalPrice);
  const appSettings = BookingGetters.getAppSettings();
  const defaultCurrency = appSettings?.defaultCurrency;
  const discount = (100 - (VehicleDetails?.discount || 0)) / 100;

  // rounds incorrectly for partial-day bookings, e.g. airport rentals).
  const ReservationDetails = BookingGetters.getBookingDetails();
  const approximateDays = computeApproximateDaysFromDates(
    ReservationDetails?.pickUpDateAndTime || Date.now(),
    ReservationDetails?.dropOffDateAndTime || Date.now()
  );
  const originalPerDayPrice =
    VehicleDetails?.reservationSourcePayLater === "true"
      ? parseFloat(VehicleDetails?.actualPrice?.toString()) / approximateDays
      : (parseFloat(VehicleDetails?.actualPrice?.toString()) * discount) /
        approximateDays;
  const originalAmountDetails = formatPriceWithCurrencySymbol(
    currency,
    originalPerDayPrice
  );
  const [tabValue, setTabValue] = useState(0);
  const { t: translate } = useTranslation();
  const lang = i18n.language;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];

  //  payment-choice-on-card support ──────────────────────────
  const [searchParams] = useSearchParams();
  const isInvalidScriptChannelResSource =
    searchParams.get("isInvalidScriptChannelResSource") == "true";
  const isPaymentModeAlreadyChosenAtSearch =
    (VehicleDetails?.reservationSourcePayNow == "true" ||
      VehicleDetails?.reservationSourcePayLater == "true") &&
    !isInvalidScriptChannelResSource;
  const showChoiceOnCard =
    !!appSettings?.showPaymentChoiceOnVehicleCard &&
    !isPaymentModeAlreadyChosenAtSearch;

  // Compute Pay Now / Pay Later amounts using the same helper used by
  // VehicleCardPitView so prices are identical between the card and popup.
  const cardBaseTotalForCard =
    VehicleDetails?.cardPaymentBaseTotal ??
    (parseFloat(VehicleDetails?.priceDetails?.payNow) || 0);
  const cardPaymentChoice =
    showChoiceOnCard && VehicleDetails && cardBaseTotalForCard > 0
      ? computePaymentChoiceOnCard({
          baseTotal: cardBaseTotalForCard,
          payNowDis: VehicleDetails?.discount,
          payLaterSurcharge: VehicleDetails?.payLaterSurcharge,
          rentalDays: approximateDays,
        })
      : null;

  // Voucher strikethrough originals (only when a voucher/discount is applied)
  const voucherOriginalPaymentChoiceForCard =
    showChoiceOnCard && VehicleDetails?.isDiscountApplied && cardPaymentChoice
      ? computeVoucherOriginalPaymentChoice({
          baseTotal: parseFloat(VehicleDetails?.actualPrice?.toString()) || 0,
          payNowDis: VehicleDetails?.discount,
          payLaterSurcharge: VehicleDetails?.payLaterSurcharge,
          rentalDays: approximateDays,
          current: cardPaymentChoice,
        })
      : null;

  // Labels (same sources as VehicleCardPitView)
  const payNowLabel =
    data.find((item) => item.key === "payNowRadioButton")?.body ||
    translate("Pay Now") ||
    "";
  const payAtPickupLabel =
    data.find((item) => item.key === "payLaterRadioButton")?.body ||
    translate("Pay Later") ||
    "";
  const perDayLabel = translate("/day");
  const totalLabel = translate("total.for.period");
  const selectLabel = translate("Select");
  const savingsAmountFormatted = cardPaymentChoice
    ? formatPriceWithCurrencySymbol(currency, cardPaymentChoice.savingsAmount)
    : { symbol: "", price: "" };
  const savingsLabel = translate("paymentChoiceOnCard.savingsBadge", {
    amount:
      (defaultCurrency || savingsAmountFormatted.symbol) +
      " " +
      savingsAmountFormatted.price,
  });
  // Sub-labels shown under column headers (same logic as BookingVehicleInfoCard / VehicleCardPitView).
  // Logic extracted to paymentChoiceOnCard.ts so all three card components share one implementation.
  const popupPayNowDiscountLabel = buildPayNowDiscountLabel(
    showChoiceOnCard,
    VehicleDetails?.discount ?? 0,
    appSettings?.hidePayNowDiscountLabel,
    translate("Discount")
  );
  const popupPayLaterSubLabel = buildPayLaterSubLabel(
    !!showChoiceOnCard,
    data,
    appSettings?.percentOnPayLaterAmount,
    VehicleDetails?.payLaterSurcharge
  );
  // ─────────────────────────────────────────────────────────────────────────

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const [selectedImage, setSelectedImage] = useState(0);

  const allImages = Array.from(
    new Set([
      ...(VehicleDetails?.vehicleExtraImage || []),
      ...(VehicleDetails?.vehicleImages || []),
    ])
  );

  const safeSelectedImage = Math.min(selectedImage, allImages.length - 1);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e: { touches: { clientX: number }[] }) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: { touches: { clientX: number }[] }) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setSelectedImage((prev) =>
          prev === allImages.length - 1 ? 0 : prev + 1
        );
      } else {
        setSelectedImage((prev) =>
          prev === 0 ? allImages.length - 1 : prev - 1
        );
      }
    }
  };
  const isSmallScreen = window.innerWidth <= 768;
  function encryptShareableUrl(url: string) {
    const secretKey = process.env.REACT_APP_SECRET_KEY;
    return CryptoJS.AES.encrypt(url, secretKey).toString();
  }
  const urlParams = new URLSearchParams(window.location.search);
  const vehicleImg = decodeURIComponent(urlParams.get("vehicleImg") || "");
  const [copyMessage, setCopyMessage] = useState("");
  const copyShareLink = function () {
    const url = new URL(window.location.href);
    url.searchParams.set(
      "vehicleInternationalCode",
      vehicleInternationalCode || ""
    );
    url.searchParams.set("vehicleStatus", VehicleDetails?.vehicleStatus);
    url.searchParams.set("vehicleImg", VehicleDetails?.vehicleImages[0] || "");
    url.searchParams.set("vehicleName", VehicleDetails?.vehicleName);
    url.searchParams.set("vehicleGroup", VehicleDetails?.vehicleGroup);

    const newUrl = url.toString();
    const encryptedUrl = encryptShareableUrl(newUrl);
    const shareUrl = `${
      window.location.origin
    }/vehcileInfoSharedURL?data=${encodeURIComponent(encryptedUrl)}`;
    navigator.clipboard.writeText(shareUrl);
    const urlCopiedText = data?.find((item) => item.key === "urlCopiedText");
    setCopyMessage(urlCopiedText?.body || translate("URL copied!"));
    setTimeout(() => {
      setCopyMessage("");
    }, 5000);
  };

  // Reusable text style
  const textStyle = (sizeSmall, sizeLarge = sizeSmall) => ({
    fontSize: isSmallScreen ? `${sizeSmall}px` : `${sizeLarge}px`,
  });

  // Shared style for the popup tabs (override `padding` per tab where needed).
  const tabSx = {
    textTransform: "none",
    fontSize: isSmallScreen ? "10px !important" : "12px !important",
    whiteSpace: isSmallScreen ? "" : "nowrap",
    minWidth: istabletScreen ? "150px !important" : "100px",
    padding: "12px",
  };

  // handles Pay Now / Pay Later selection made inside the popup.
  // Mirrors the handlePaymentChoiceSelect logic in VehicleCardPitView.
  const handleCardPaymentSelect = (mode: "payNow" | "payAtPickup") => {
    setIsLoading(true);
    closeModal();
    window.scrollTo(0, 0);
    VehicleDetails?.moveToPayment({
      isPayLaterFee: false,
      extrasIncluded: false,
      extrasPrice: 0,
      payLaterFeeAmt: "",
      isDiscounted: mode === "payNow" ? payNowDisAmount : 0,
      type: mode === "payNow" ? "PAYNOW" : "PAYLATER",
      price:
        mode === "payNow"
          ? cardPaymentChoice?.payNowTotal ?? totalPrice
          : cardPaymentChoice?.payAtPickupTotal ?? totalPrice,
      addedExtraOptions: selectedFields?.options,
      selectedInsurance: selectedFields?.insurance,
      surchargeAmt: "0",
    });
  };

  function BookButton() {
    return (
      <AppButton
        disabled={
          VehicleDetails?.vehicleStatus?.toLowerCase() === "unavailable"
        }
        color="primary"
        className="w-[6rem] h-[3rem] rounded-md"
        clickHandler={() => {
          setIsLoading(true);
          closeModal();
          window.scrollTo(0, 0);
          VehicleDetails?.moveToPayment({
            isPayLaterFee: false,
            extrasIncluded: false,
            extrasPrice: 0,
            payLaterFeeAmt: "",
            isDiscounted: payNowDisAmount,
            type: paymentType as "PAYNOW" | "PAYLATER",
            price: totalPrice,
            addedExtraOptions: selectedFields.options,
            selectedInsurance: selectedFields.insurance,
          });
        }}
        style={{
          borderRadius: `${appSettings?.buttonBorderRadius || 0}px`,
        }}
      >
        <p
          className={`min-w-24 w-full h-full flex items-center justify-center text-${VehicleDetails?.textColor} text-x capitalize font-normal`}
          style={{ textTransform: "none" }}
        >
          <span>{translate("Forward")}</span>
        </p>
      </AppButton>
    );
  }

  const showForScriptChannel =
    (scriptChannel &&
      ((VehicleDetails?.showScalapayWidgetPayNow && paymentType == "PAYNOW") ||
        (VehicleDetails?.showScalapayWidgetPayLater &&
          paymentType == "PAYLATER"))) ||
    (VehicleDetails?.reservationSourcePayNow == "true" &&
      VehicleDetails?.showScalapayWidgetPayNow) ||
    (VehicleDetails?.reservationSourcePayLater == "true" &&
      VehicleDetails?.showScalapayWidgetPayLater);

  const showForNonScriptChannel =
    scriptChannel == "" &&
    VehicleDetails?.reservationSourcePayNow === "false" &&
    VehicleDetails?.reservationSourcePayLater === "false" &&
    (VehicleDetails?.showScalapayWidgetPayNow === true ||
      VehicleDetails?.showScalapayWidgetPayLater === true);
  const isReservedAreaUser = !!localStorage.getItem("userID");
  const shouldShowScalapayWidget =
    !isReservedAreaUser && (showForScriptChannel || showForNonScriptChannel);

  useEffect(() => {
    onScalapayWidgetChange(shouldShowScalapayWidget);
  }, [shouldShowScalapayWidget]);

  return (
    <Box
      className="bg-white rounded-2xl max-w-5xl flex flex-col md:flex-row"
      sx={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        width: "100%",
      }}
    >
      {!isVehicleUnavQuot && (
        <>
          <IconButton
            aria-label="share"
            sx={{
              position: "absolute",
              top: 10,
              right: 14,
              color: "black",
              "&:hover": { color: "black" },
              marginRight: "32px",
            }}
            onClick={() => copyShareLink()}
          >
            <ShareIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 14,
              color: "black",
              "&:hover": { color: "black" },
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          {copyMessage && (
            <Typography
              sx={{
                position: "absolute",
                top: 45,
                right: 25,
                color: dynamicTheme?.palette["primary"]?.main,
                fontSize: "14x",
                fontWeight: "bold",
              }}
            >
              {copyMessage}
            </Typography>
          )}
        </>
      )}
      {/* Car Image Section */}
      <Box
        className="flex-1 flex flex-col items-center"
        sx={{
          padding: "24px",
          overflow: "hidden",
          minHeight: 0,
          ...(isSmallScreen && {
            flex: "0 0 auto",
            height: "150px",
          }),
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            height: "90%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: copyMessage && isSmallScreen ? "20px" : "0px",
          }}
        >
          <img
            src={isVehicleUnavQuot ? vehicleImg : allImages[safeSelectedImage]}
            alt="vehicle"
            className="object-contain rounded-xl"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
        </Box>

        {/* Dots Navigation */}
        {allImages.length > 1 && (
          <Box
            sx={{
              height: "10%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="flex space-x-2">
              {allImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-[8px] h-[8px] rounded-full transition ${
                    index === safeSelectedImage ? "bg-black" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </Box>
        )}
      </Box>

      {/* Details Section */}
      <Box
        className="flex-1 flex flex-col"
        sx={{
          backgroundColor: "#EEEEEE",
          padding: "24px",
          borderTopRightRadius: "16px",
          borderTopLeftRadius: isSmallScreen ? "16px" : "0",
          borderBottomRightRadius: "16px",
          borderBottomLeftRadius: isSmallScreen ? "16px" : "",
          paddingTop: copyMessage && !isSmallScreen ? "40px" : "24px",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* FIXED: vehicle title — never scrolls */}
        <Typography variant="subtitle2" sx={textStyle(12, 14)}>
          {translate("Group")}{" "}
          {isVehicleUnavQuot
            ? urlParams.get("vehicleGroup")
            : VehicleDetails?.vehicleGroup}
        </Typography>
        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={textStyle(24, 32)}
        >
          {isVehicleUnavQuot
            ? urlParams.get("vehicleName")
            : VehicleDetails?.vehicleName}
        </Typography>

        {/* FIXED: tabs bar — never scrolls */}
        {!isVehicleUnavQuot && (
          <Tabs value={tabValue} onChange={handleChange} variant="fullWidth">
            <Tab label={translate("Technical data sheet")} sx={tabSx} />
            <Tab label={translate("Deductible and deposit")} sx={tabSx} />
            <Tab
              label={translate("Included services")}
              sx={{ ...tabSx, padding: "8px" }}
            />
          </Tabs>
        )}

        {/* FIXED: tab description texts — never scroll */}
        {!isVehicleUnavQuot && tabValue === 0 && modalTechnicalDataContent && (
          <Typography
            sx={{ ...textStyle(10, 14), mt: 2, mb: 1 }}
            variant="body2"
            color="text.secondary"
          >
            {modalTechnicalDataContent}
          </Typography>
        )}
        {!isVehicleUnavQuot && tabValue === 1 && modalDeductibleDepositContent && (
          <Typography
            sx={{ ...textStyle(10, 14), mt: 2, mb: 1 }}
            variant="body2"
            color="text.secondary"
          >
            {modalDeductibleDepositContent}
          </Typography>
        )}

        {/* SCROLLABLE: only the data content scrolls */}
        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {isVehicleUnavQuot && (
            <Box
              className="flex flex-col items-center justify-center flex-1 text-center"
              sx={{
                padding: "32px",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "12px",
                  fontSize: isSmallScreen ? "18px" : "22px",
                }}
              >
                {translate("Vehicle Unavailable")}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: isSmallScreen ? "12px" : "14px",
                  marginBottom: "24px",
                }}
              >
                {translate("Sorry, this vehicle is currently unavailable.")}
              </Typography>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#00ADEF",
                  borderRadius: "8px",
                  textTransform: "none",
                  paddingX: "28px",
                  "&:hover": { backgroundColor: "#0097D1" },
                }}
                onClick={onClose}
              >
                {translate("Close")}
              </Button>
            </Box>
          )}

          {!isVehicleUnavQuot && tabValue === 0 && (
            <Box
              className="border rounded-xl"
              sx={{ marginTop: "16px", marginBottom: "16px" }}
            >
              <Grid container spacing={2}>
                {VehicleDetails?.specifications?.map((spec, index) => (
                  <Grid item xs={6} key={index}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <img
                        src={spec.iconUrl}
                        alt="specIcon"
                        style={{
                          height: "20px",
                          width: "20px",
                          borderRadius: "15px",
                        }}
                      />
                      <Typography sx={{ ...textStyle(10, 14), color: "#000" }}>
                        {spec.name}: {spec.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/*Deductible and deposit*/}
          {!isVehicleUnavQuot && tabValue === 1 && (
            <Box
              className="border rounded-xl divide-y divide-black"
              sx={{
                textTransform: "uppercase",
                backgroundColor: "#FFFFFF",
                border: "1px solid black",
                borderRadius: "10px",
                mt: 1,
              }}
            >
              {[
                {
                  icon: "/assets/icons/FranchigiaFurto.png",
                  label: translate("theft.excess.amounts"),
                  value:
                    appSettings?.showTheftExcessPercentage &&
                    VehicleDetails?.theftExcessPercentage
                      ? `${VehicleDetails.theftExcessPercentage}%`
                      : `${defaultCurrency || originalAmountDetails.symbol} ${
                          VehicleDetails?.theftExcessAmount
                        }`,
                },
                {
                  icon: "/assets/icons/FranchigiaDANNI.png",
                  label: translate("damage.excess.amounts"),
                  value: `${defaultCurrency || originalAmountDetails.symbol} ${
                    VehicleDetails?.damageExcessAmount
                  }`,
                },

                {
                  icon: "/assets/icons/DepositoCauzionale.png",
                  label: translate("deposit.excess.amounts"),
                  value: `${defaultCurrency || originalAmountDetails.symbol} ${
                    VehicleDetails?.DepositAmount
                  }`,
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  className="flex items-center justify-between p-3"
                >
                  <Box className="flex items-center gap-3 min-w-0">
                    <img
                      src={item.icon}
                      alt={item.label}
                      className="w-[17px] h-[14px] shrink-0"
                    />
                    <Typography noWrap sx={textStyle(10, 14)}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ ...textStyle(10, 14), whiteSpace: "nowrap" }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/*Included services*/}
          {!isVehicleUnavQuot && tabValue === 2 && (
            <Box
              className="border rounded-xl space-y-3"
              sx={{
                backgroundColor: "#FFF",
                border: "1px solid black",
                borderRadius: "10px",
                mt: 2,
                p: 2,
              }}
            >
              {["Included", "Not Including"].map((section) => {
                const isIncluded = section === "Included";
                const extras = VehicleDetails?.extras?.filter(
                  (e) => e?.Charge?.IncludedInRate === isIncluded
                );
                return (
                  <Box key={section}>
                    <Box
                      sx={{
                        backgroundColor: isIncluded
                          ? dynamicTheme?.palette["secondaryColor"]?.main
                          : "#00000033",
                        borderRadius: 16,
                        px: 1.5,
                        py: 0.5,
                        width: "fit-content",
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ ...textStyle(10, 12), color: "#FFF" }}>
                        {translate(section)}
                      </Typography>
                    </Box>
                    <Grid container spacing={1} ml={isSmallScreen ? 0 : 0.5}>
                      {extras?.length
                        ? extras.map((extra, i) => (
                            <Grid item xs={isIncluded ? 6 : 4} key={i}>
                              <Typography
                                sx={{
                                  ...textStyle(8, 10),
                                  color: "#000",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {extra.details}
                              </Typography>
                            </Grid>
                          ))
                        : isIncluded && (
                            <Grid item xs={12}>
                              <Typography
                                sx={{ ...textStyle(8, 10), color: "gray" }}
                              >
                                {translate("No included extras available.")}
                              </Typography>
                            </Grid>
                          )}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer */}
        {!isVehicleUnavQuot && (
          //  when card-choice flag is ON, replace the single price + button with the PaymentChoiceBlock component.
          <Box
            className={
              showChoiceOnCard
                ? "mt-3"
                : "flex justify-between items-center mt-6"
            }
            sx={
              showChoiceOnCard
                ? {}
                : { paddingTop: "8px", borderTop: "1px solid #D9D9D9" }
            }
          >
            {showChoiceOnCard && cardPaymentChoice ? (
              <div
                style={
                  isSmallScreen
                    ? {
                        transform: "scale(0.82)",
                        transformOrigin: "top center",
                        marginBottom: "-18%",
                      }
                    : undefined
                }
              >
                <PaymentChoiceBlock
                  compact
                  payNowDaily={cardPaymentChoice.payNowDaily}
                  payNowTotal={cardPaymentChoice.payNowTotal}
                  payAtPickupDaily={cardPaymentChoice.payAtPickupDaily}
                  payAtPickupTotal={cardPaymentChoice.payAtPickupTotal}
                  showSavingsBadge={cardPaymentChoice.showSavingsBadge}
                  savingsAmount={cardPaymentChoice.savingsAmount}
                  currencyCode={currency}
                  defaultCurrency={defaultCurrency}
                  perDayLabel={perDayLabel}
                  totalLabel={totalLabel}
                  selectLabel={selectLabel}
                  savingsLabel={savingsLabel}
                  payAtPickupLabel={payAtPickupLabel}
                  payNowLabel={payNowLabel}
                  onSelect={handleCardPaymentSelect}
                  disabled={
                    VehicleDetails?.vehicleStatus?.toLowerCase() ===
                    "unavailable"
                  }
                  buttonBorderRadius={appSettings?.buttonBorderRadius}
                  originalPayNowDaily={
                    voucherOriginalPaymentChoiceForCard?.originalPayNowDaily
                  }
                  originalPayNowTotal={
                    voucherOriginalPaymentChoiceForCard?.originalPayNowTotal
                  }
                  originalPayAtPickupDaily={
                    voucherOriginalPaymentChoiceForCard?.originalPayAtPickupDaily
                  }
                  originalPayAtPickupTotal={
                    voucherOriginalPaymentChoiceForCard?.originalPayAtPickupTotal
                  }
                  showOriginalPayNow={
                    voucherOriginalPaymentChoiceForCard?.showOriginalPayNow
                  }
                  showOriginalPayAtPickup={
                    voucherOriginalPaymentChoiceForCard?.showOriginalPayAtPickup
                  }
                  payNowDiscountLabel={popupPayNowDiscountLabel}
                  payLaterSubLabel={popupPayLaterSubLabel}
                />
              </div>
            ) : (
              <>
                <Box>
                  <Box sx={{ marginBottom: "-9px" }}>
                    {VehicleDetails?.isDiscountApplied && (
                      <Typography
                        component="p"
                        sx={{
                          ...textStyle(14, 16),
                          textDecoration: "line-through",
                          color: dynamicTheme?.palette["primary"]?.main,
                          fontWeight: 700,

                          lineHeight: 1,
                          marginBottom: "2px",
                        }}
                      >
                        {(defaultCurrency || originalAmountDetails.symbol) +
                          " " +
                          originalAmountDetails.price}
                        <Typography
                          component="span"
                          sx={{ fontWeight: 400, fontSize: "0.85em" }}
                        >
                          {translate("/day")}
                        </Typography>
                      </Typography>
                    )}
                    <Typography sx={{ ...textStyle(24, 28) }} fontWeight="bold">
                      {(defaultCurrency || discountedAmountDetails.symbol) +
                        " " +
                        discountedAmountDetails.price}
                      <Typography component="span" fontSize={18}>
                        {translate("/day")}
                      </Typography>
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      ...textStyle(10, 12),
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    {VehicleDetails?.isDiscountApplied && (
                      <span
                        style={{
                          textDecoration: "line-through",
                          color: dynamicTheme?.palette["primary"]?.main,
                          marginRight: "4px",
                        }}
                      >
                        {(defaultCurrency || amountDetails.symbol) +
                          (VehicleDetails.reservationSourcePayLater === "true"
                            ? parseFloat(VehicleDetails.actualPrice.toString())
                            : parseFloat(
                                VehicleDetails.actualPrice.toString()
                              ) * discount
                          ).toFixed(2)}
                      </span>
                    )}
                    <span>
                      {(defaultCurrency || amountDetails.symbol) +
                        " " +
                        amountDetails.price}
                    </span>
                    <span>{translate("total.for.period")}</span>
                  </Typography>
                </Box>
                <BookButton />
              </>
            )}
          </Box>
        )}
        {shouldShowScalapayWidget && (
          <div style={{ paddingTop: "5px" }}>
            <hr style={{ border: ".3px solid #ccc" }} />
            <div
              style={{
                paddingTop: "4px",
                fontSize: "12px",
                color: "#000",
              }}
            >
              <scalapay-widget
                locale={localeScalapay}
                channel="travel"
                amount={totalPrice.toFixed(2)}
                type="product"
                merchant-token={VehicleDetails.scalapayMerchantToken}
                {...(VehicleDetails?.scalapayEnvVal !== ""
                  ? { environment: VehicleDetails?.scalapayEnvVal }
                  : {})}
              />
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
}

export default CarDetailsPopup;
